
// ------------------------------------------------------------------
// 🔔 NOTIFICATION SYSTEM TYPES
// ------------------------------------------------------------------

export enum NotificationCategory {
  MARKETPLACE = 'MARKETPLACE',
  ORDERS = 'ORDERS',
  FINANCIAL = 'FINANCIAL',
  SOCIAL = 'SOCIAL',
  SECURITY = 'SECURITY',
  ADMIN = 'ADMIN',
  SYSTEM = 'SYSTEM',
  MARKETING = 'MARKETING'
}

export enum NotificationType {
  // Marketplace
  LISTING_APPROVED = 'LISTING_APPROVED',
  LISTING_REJECTED = 'LISTING_REJECTED',
  LISTING_EXPIRED = 'LISTING_EXPIRED',
  LISTING_SOLD = 'LISTING_SOLD',
  LISTING_FAVORITED = 'LISTING_FAVORITED',
  PRICE_DROP_ALERT = 'PRICE_DROP_ALERT',
  BACK_IN_STOCK = 'BACK_IN_STOCK',
  SIMILAR_LISTING_ALERT = 'SIMILAR_LISTING_ALERT',

  // Orders
  ORDER_PLACED = 'ORDER_PLACED',
  ORDER_CONFIRMED = 'ORDER_CONFIRMED',
  ORDER_SHIPPED = 'ORDER_SHIPPED',
  ORDER_DELIVERED = 'ORDER_DELIVERED',
  ORDER_CANCELLED = 'ORDER_CANCELLED',
  ORDER_DISPUTE_OPENED = 'ORDER_DISPUTE_OPENED',
  ORDER_DISPUTE_RESOLVED = 'ORDER_DISPUTE_RESOLVED',
  ORDER_REVIEW_REQUEST = 'ORDER_REVIEW_REQUEST',
  ORDER_REMINDER_SHIP = 'ORDER_REMINDER_SHIP',
  ORDER_REMINDER_CONFIRM = 'ORDER_REMINDER_CONFIRM',

  // Seller Approval Flow
  ORDER_AWAITING_APPROVAL = 'ORDER_AWAITING_APPROVAL', // Seller'a gider
  ORDER_APPROVED = 'ORDER_APPROVED',                   // Buyer'a gider
  ORDER_REJECTED = 'ORDER_REJECTED',                   // Buyer'a gider
  ORDER_AUTO_CANCELLED = 'ORDER_AUTO_CANCELLED',       // Her ikisine gider (Zaman aşımı)

  // Financial
  PAYMENT_RECEIVED = 'PAYMENT_RECEIVED',
  PAYMENT_SENT = 'PAYMENT_SENT',
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  PAYMENT_RELEASED = 'PAYMENT_RELEASED',
  REFUND_PROCESSED = 'REFUND_PROCESSED',
  WITHDRAWAL_APPROVED = 'WITHDRAWAL_APPROVED',
  WITHDRAWAL_REJECTED = 'WITHDRAWAL_REJECTED',
  WITHDRAWAL_COMPLETED = 'WITHDRAWAL_COMPLETED',
  DEPOSIT_SUCCESS = 'DEPOSIT_SUCCESS',
  LOW_BALANCE_WARNING = 'LOW_BALANCE_WARNING',
  COMMISSION_DEDUCTED = 'COMMISSION_DEDUCTED',

  // Social
  NEW_FOLLOWER = 'NEW_FOLLOWER',
  NEW_MESSAGE = 'NEW_MESSAGE',
  MESSAGE_READ = 'MESSAGE_READ',
  REVIEW_RECEIVED = 'REVIEW_RECEIVED',
  REVIEW_REPLY = 'REVIEW_REPLY',
  OFFER_RECEIVED = 'OFFER_RECEIVED',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
  OFFER_REJECTED = 'OFFER_REJECTED',
  OFFER_COUNTERED = 'OFFER_COUNTERED',
  MENTION_IN_COMMENT = 'MENTION_IN_COMMENT',
  QUESTION_RECEIVED = 'QUESTION_RECEIVED',
  REPLY_RECEIVED = 'REPLY_RECEIVED',

  // Security
  LOGIN_NEW_DEVICE = 'LOGIN_NEW_DEVICE',
  LOGIN_NEW_LOCATION = 'LOGIN_NEW_LOCATION',
  PASSWORD_CHANGED = 'PASSWORD_CHANGED',
  EMAIL_CHANGED = 'EMAIL_CHANGED',
  TWO_FACTOR_ENABLED = 'TWO_FACTOR_ENABLED',
  TWO_FACTOR_DISABLED = 'TWO_FACTOR_DISABLED',
  SUSPICIOUS_ACTIVITY = 'SUSPICIOUS_ACTIVITY',
  ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
  FAILED_LOGIN_ATTEMPTS = 'FAILED_LOGIN_ATTEMPTS',

  // Admin
  RESTRICTION_APPLIED = 'RESTRICTION_APPLIED',
  RESTRICTION_REMOVED = 'RESTRICTION_REMOVED',
  APPEAL_STATUS_UPDATED = 'APPEAL_STATUS_UPDATED',
  ACCOUNT_WARNING = 'ACCOUNT_WARNING',
  CONTENT_REMOVED = 'CONTENT_REMOVED',
  VERIFICATION_APPROVED = 'VERIFICATION_APPROVED',
  VERIFICATION_REJECTED = 'VERIFICATION_REJECTED',
  BOOST_APPROVED = 'BOOST_APPROVED',
  BOOST_REJECTED = 'BOOST_REJECTED',

  // System
  MAINTENANCE_SCHEDULED = 'MAINTENANCE_SCHEDULED',
  MAINTENANCE_STARTED = 'MAINTENANCE_STARTED',
  MAINTENANCE_COMPLETED = 'MAINTENANCE_COMPLETED',
  FEATURE_ANNOUNCEMENT = 'FEATURE_ANNOUNCEMENT',
  TERMS_UPDATED = 'TERMS_UPDATED',
  POLICY_UPDATED = 'POLICY_UPDATED',
  APP_UPDATE_AVAILABLE = 'APP_UPDATE_AVAILABLE',
  SERVICE_DISRUPTION = 'SERVICE_DISRUPTION',

  // Marketing
  PROMOTIONAL_OFFER = 'PROMOTIONAL_OFFER',
  SEASONAL_SALE = 'SEASONAL_SALE',
  REFERRAL_BONUS = 'REFERRAL_BONUS',
  LOYALTY_REWARD = 'LOYALTY_REWARD',
  PERSONALIZED_RECOMMENDATION = 'PERSONALIZED_RECOMMENDATION',
  NEWSLETTER = 'NEWSLETTER',

  // Legacy (Backward Compatibility)
  SALE = 'SALE',
  MESSAGE = 'MESSAGE',
  FAVORITE = 'FAVORITE',
  SYSTEM = 'SYSTEM'
}

export type NotificationEntityType = 'listing' | 'order' | 'transaction' | 'message' | 'user';

export type DeliveryStatus = 'pending' | 'sent' | 'delivered' | 'bounced' | 'failed';

export type NotificationPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface Notification {
  id: string;
  userId: string;
  category: NotificationCategory;
  type: NotificationType;
  priority: NotificationPriority;

  title: string;
  message: string;
  richContent?: {
    imageUrl?: string;
    ctaText?: string;
    ctaUrl?: string;
    actionButtons?: Array<{
      label: string;
      action: string;
      variant: 'primary' | 'secondary' | 'danger';
    }>;
  };

  actorId?: string;
  actorName?: string;
  actorAvatar?: string;

  relatedEntityId?: string;
  relatedEntityType?: NotificationEntityType;
  listingId?: string; // Legacy support

  isRead: boolean;
  isArchived: boolean;
  isActionable: boolean;
  actionTaken?: string;

  channels: {
    inApp: boolean;
    email: boolean;
    push: boolean;
    sms: boolean;
  };

  deliveryStatus: {
    inApp: 'pending' | 'delivered' | 'failed';
    email?: 'pending' | 'sent' | 'delivered' | 'bounced' | 'failed';
    push?: 'pending' | 'sent' | 'delivered' | 'failed';
    sms?: 'pending' | 'sent' | 'delivered' | 'failed';
  };

  createdAt: number;
  readAt?: number;
  archivedAt?: number;
  expiresAt?: number;
  scheduledFor?: number;

  metadata?: Record<string, any>;
  data?: any; // Legacy support
  groupKey?: string;

  // Battle Plan Week 1 Enhancements
  targetUrl?: string;
  isPromotional?: boolean;
  isUninterruptible?: boolean; // Critical security/financial bypass
}

export interface ChannelPreferences {
  inApp: boolean;
  email: boolean;
  push: boolean;
  sms: boolean;
}

export interface NotificationPreferences {
  userId: string;

  categories: {
    [key in NotificationCategory]: ChannelPreferences;
  };

  doNotDisturb: {
    enabled: boolean;
    schedule?: {
      start: string;
      end: string;
    };
  };

  groupSimilar: boolean;
  digestEnabled: boolean;
  digestFrequency: 'daily' | 'weekly';

  updatedAt: number;
}

export enum ListingType {
  REGULAR = 'REGULAR',
  EMERGENCY = 'EMERGENCY',
  SWAP = 'SWAP'
}

export enum ListingStatus {
  ACTIVE = 'ACTIVE',
  PENDING_REVIEW = 'PENDING_REVIEW',
  OFFER_MADE = 'OFFER_MADE',
  SOLD = 'SOLD',
  REJECTED = 'REJECTED',
  VACATION = 'VACATION'
}

export type Gender = 'male' | 'female' | 'other' | 'none';

export interface VisualAnalysis {
  brand?: string;
  detectedCondition: 'new' | 'like_new' | 'used' | 'worn';
  authenticityConfidence: number;
  visualRedFlags: string[];
  descriptionMatch: boolean;
}

export interface Listing {
  id: string;
  title: string;
  description: string;
  price: number;
  city?: string;
  district?: string; // Ilce
  neighborhood?: string; // Mahalle (Optional/Manual for now)
  street?: string; // Sokak (Optional/Manual for now)

  // Advanced Categorization
  categoryId: string; // Main Category ID (e.g., 'electronics')
  category: string; // Display Name (kept for legacy/display compatibility)
  subCategoryId?: string; // e.g., 'phones'
  productTypeId?: string; // e.g., 'smartphones'

  attributes?: Record<string, string | number | boolean>; // Dynamic attributes (e.g., { brand: 'Apple', storage: '128 GB' })

  brand?: string; // Kept for top-level access if needed, or mapped from attributes
  condition: 'new' | 'like_new' | 'used' | 'worn';
  type: ListingType;
  status: ListingStatus;
  imageUrls: string[];
  sellerId: string;
  createdAt: number;
  created_at?: number;
  updatedAt: number;
  visualAnalysis?: VisualAnalysis;
  boostedUntil?: number;
  viewCount?: number;
  cartCount?: number;
  promotionType?: 'NONE' | 'FEE_BASED' | 'FIXED';
  promotionExpiresAt?: number;
  offerAmount?: number;
  favoritesCount?: number;

  // 🌍 Multi-Region Support (Region Lock)
  marketRegion?: 'TR' | 'US' | 'UK';  // Listing is only visible in this region
  currency?: 'TRY' | 'USD' | 'GBP';   // Price currency

  // 🚀 Doping (Boost) Fields (Faz 8)
  dopingActive?: boolean;
  dopingEndDate?: number;
  dopingType?: 'DIRECT' | 'COMMISSION';
  dopingPackage?: 1 | 3 | 7;
  dopingPrice?: number;

  // ⚡ Flash Sale & Shipping Fields (Faz 56)
  originalPrice?: number;
  discountPercentage?: number;
  discountEndDate?: number;
  shippingPayer?: 'buyer' | 'seller';
  packageSize?: 'xs' | 's' | 'm' | 'l' | 'xl';
  shippingPrice?: number;
  commissionRate?: number;
  taxAmount?: number;
  referralFee?: number;
  finalPayout?: number;

  // 🛡️ Advanced Transaction & Logs (Lalegül)
  priceHistory?: { price: number; timestamp: number }[];
  shippingSpecs?: {
    weight?: number;
    length?: number;
    width?: number;
    height?: number;
    payer: 'buyer' | 'seller';
    size: 'xs' | 's' | 'm' | 'l' | 'xl';
  };
  metrics?: {
    views: number;
    clicks: number;
    salesScore: number;
    returnRate: number;
  };
  pendingUpdate?: Partial<Listing>;
  isBridgeOnly?: boolean;
  fitsInCarTrunk?: boolean;
  offersDisabled?: boolean;
}

export interface SystemConfig {
  commissionRates: {
    default: number; // Legacy or Total? Let's keep for traverse compatibility but rely on explicit below
    buyer: number;
    seller: number;
    referral: number;
    electronics?: number;
    clothing?: number;
  };
  dopingRates: {
    day1: { base: number; percentage: number };
    day3: { base: number; percentage: number };
    day7: { base: number; percentage: number };
  };
  boostStats: {
    riskFreeRate: number;
    fixedPackages?: {
      day1: number;
      day3: number;
      day7: number;
    };
  };
}

export interface Favorite {
  id: string;
  userId: string;
  listingId: string;
  listingData?: Partial<Listing>;
  createdAt: Date | any;
}

// Seller Badge Tiers
export type BadgeTier = 'new' | 'rising' | 'trusted' | 'elite' | 'legend';

// Review Status & Types
export type ReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type DisputeStatus = 'NONE' | 'PENDING' | 'RESOLVED';

export interface Review {
  id: string;
  orderId: string;
  listingId: string;
  sellerId: string;
  buyerId: string;
  rating: number;                     // 1-5
  comment?: string;
  sellerReply?: string;
  replyStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  replyAt?: number;
  isAnonymous: boolean;
  status: ReviewStatus;
  isSystemGenerated: boolean;
  moderationNote?: string;
  disputeStatus?: DisputeStatus;
  disputeReason?: string;
  createdAt: number;
  buyerName?: string;
  buyerAvatar?: string;
  listingTitle?: string;
  listingImage?: string;
}


// ------------------------------------------------------------------
// ⚡ INSTANT BUY (HIZLI SAT) SYSTEM TYPES
// ------------------------------------------------------------------

export enum InstantBuyStatus {
  PENDING = 'PENDING',               // User submitted request
  REVIEWING = 'REVIEWING',           // Admin is checking photos/vids
  OFFER_MADE = 'OFFER_MADE',         // Admin made a counter/final offer
  OFFER_REJECTED = 'OFFER_REJECTED', // User declined the price
  ACCEPTED = 'ACCEPTED',             // User accepted, waiting for shipping selection
  SHIPPING_CODE_GENERATED = 'SHIPPING_CODE_GENERATED', // Platform gave a code, seller needs to go to branch
  SHIPPING = 'SHIPPING',             // Product is on the way to platform
  RECEIVED = 'RECEIVED',             // Platform received the package
  INSPECTING = 'INSPECTING',         // Technical team is inspecting for hidden defects
  PAYMENT_PENDING = 'PAYMENT_PENDING',// Inspection OK, payment in queue
  PAID = 'PAID',                     // IBAN transfer completed
  REVISED_OFFER = 'REVISED_OFFER',   // Item condition differs, new price offered
  CANCELLED = 'CANCELLED',           // Request cancelled by either party
}

export interface InstantBuyRequest {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  sellerName?: string; // T.C. Kimlik üzerindeki tam ad
  tcNumber?: string;   // 11 Haneli T.C. No (Gider pusulası için)
  category: string;
  model: string;
  specs: Record<string, any>;
  condition: 'new' | 'like_new' | 'used' | 'worn';
  imei?: string;
  serialNumber?: string;
  photos: {
    label: string;
    url: string;
  }[];
  videoUrl?: string;
  askingPrice: number;
  offeredPrice?: number;
  status: InstantBuyStatus;
  payoutIban: string;
  adminNote?: string;
  revisedPrice?: number;             // New price if original condition check failed
  rejectionReason?: string;          // Why the device was rejected
  returnTrackingCode?: string;       // Tracking for returned rejected items
  technicianNote?: string;           // Final tech summary
  returnAddress?: Address;           // Return address details (PII)
  shippingCarrier?: string;          // Selected courier (Yurtiçi, Aras, etc)
  shippingCode?: string;             // System generated code for the branch
  shippingTrackingCode?: string;     // Actual tracking code
  createdAt: number;
  updatedAt: number;
}

// ⚡ RESTRICTION SYSTEM TYPES ⚡
// Based on expert feedback - single source of truth architecture
// Simplified: Only admin/owner can manage restrictions (no MOD/TRUST complexity)

export type RestrictionType =
  | 'WARNING'
  | 'LISTING_BAN'
  | 'SELLING_BAN'
  | 'BUYING_BAN'
  | 'MESSAGING_BAN'
  | 'WITHDRAWAL_BAN'
  | 'SUSPENSION'
  | 'PERMANENT_BAN'
  | 'SOFT_BAN';

export type RestrictionCategory =
  | 'FRAUD'
  | 'POLICY'
  | 'BEHAVIOR'
  | 'FINANCIAL'
  | 'DISPUTES'
  | 'IDENTITY';

export interface Restriction {
  id: string;
  type: RestrictionType;

  // ⚡ Only field Admin changes to toggle restriction
  active: boolean;

  reason: {
    category: RestrictionCategory;
    subcategory: string; // e.g., 'fake_listing', 'abusive_language'
    publicMessage: string; // What user sees
    internalNote: string;  // Admin eyes only
  };

  duration: {
    type: 'TEMPORARY' | 'PERMANENT' | 'UNDER_REVIEW';
    days?: number; // if temporary
    expiresAt?: number; // Auto-calculated timestamp
  };

  adminInfo: {
    adminId: string;
    adminName?: string; // Admin's display name
    appliedAt: number;
    removedAt?: number;
    removalReason?: string;
  };

  // ⚡ NEW: Appeal system
  appeal?: {
    submittedAt: number;
    userMessage: string;
    evidenceUrls?: string[]; // Optional file uploads
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    reviewedBy?: string; // Admin ID
    reviewedAt?: number;
    adminResponse?: string; // Admin's decision reason
  };
}

// Soft Ban (Shadow Ban) - invisible throttling
export interface SoftBan {
  active: boolean;
  listingVisibilityMultiplier: number; // 0.3 = shown 30% as often
  messageDelaySeconds: number; // Artificial delay
  searchRankingPenalty: number; // -50 = drops in search results

  reason: string;
  appliedAt: number;
  appliedBy: string;
}

// Blacklist entry - risk-based, not absolute blocking
export interface BlacklistEntry {
  id: string;
  type: 'IP' | 'DEVICE' | 'IBAN' | 'EMAIL';
  valueHash: string; // SHA-256 hashed for security

  // ✅ Risk-based approach (not auto-ban)
  riskScoreImpact: number; // +40 points to user's restrictionScore
  triggerManualReview: boolean; // Queue for admin review

  userId: string;
  reason: string;
  addedBy: string;
  addedAt: number;
  expiresAt?: number;
}

// Simplified: All restriction types available to admin/owner
// No need for permission mapping - single source management


// ================= ADDRESS SYSTEM =================
export interface Address {
  id: string;
  title: string;       // Örn: "Evim", "İş Yeri"
  fullName: string;    // Teslim alacak kişi (ZORUNLU)
  phone: string;       // İletişim numarası (ZORUNLU, +90 formatında)
  city: string;        // İl
  district: string;    // İlçe
  neighborhood: string; // Mahalle
  street: string;      // Sokak
  buildingNo: string;  // Bina No
  floor?: string;      // Kat (Opsiyonel)
  apartmentNo: string; // Daire No
  zipCode?: string;    // Posta Kodu
  isDefault: boolean;  // Varsayılan adres mi?
  createdAt: number;
}


export interface User {
  id: string;
  name: string;
  username: string;
  email: string;
  role: 'user' | 'support' | 'admin' | 'owner';
  avatarUrl?: string;
  balance: number;
  reservedBalance?: number; // 🔒 Locked balance for pending orders
  joinDate: number;
  bio?: string;
  legalName?: string;      // ⚖️ For GİB Reporting
  tcknVkn?: string;       // ⚖️ Encrypted TCKN or VKN
  legalAddress?: string;  // ⚖️ Formal Billing Address
  gender?: Gender;
  birthDate?: string;
  reviews?: any[];
  totalSales?: number;
  avgRating?: number;
  totalReviews?: number;
  systemReviewCount?: number;
  avgShippingDays?: number;
  trustScore?: number;
  isSaleRestricted?: boolean;
  badgeTier?: BadgeTier;
  followersCount?: number;
  favoritesCount?: number;
  referredBy?: string;
  // E-commerce & Storefront Fields
  userType?: 'INDIVIDUAL' | 'COMMERCIAL';
  storeName?: string;
  city?: string;
  district?: string;
  successfulTradesCount?: number;
  avgResponseTime?: string; // e.g., "1 saat içinde"
  isIdentityVerified?: boolean;
  lastSeen?: number;
  isVacationMode?: boolean;
  preferredCarrier?: string;
  emailVerified?: boolean;
  pinnedListingIds?: string[];

  // 🌍 Multi-Region Support (i18n)
  preferredRegion: 'TR' | 'US' | 'UK';
  preferredLanguage: 'tr-TR' | 'en-US' | 'en-GB';
  preferredCurrency: 'TRY' | 'USD' | 'GBP';
  marketRegion?: 'TR' | 'US' | 'UK'; // Alias for consistency with listings/orders
  regionLockedAt?: number;  // Timestamp when region was locked
  followingIds?: string[];
  favorites?: string[];
  // Security & Risk Fields
  lastIp?: string;
  deviceId?: string;
  riskScore?: number; // 0-100
  riskStatus?: 'SAFE' | 'SUSPICIOUS' | 'DANGEROUS';
  verifiedIbans?: string[];
  riskSignals?: {
    ipConflict: boolean;
    deviceConflict: boolean;
    ibanConflict: boolean;
  };
  lastRiskDecayAt?: number;
  premiumEligible?: boolean;
  trustBreakdown?: {
    salesSuccess: number;
    responseSpeed: number;
    shippingSpeed: number;
    accountAge: number;
  };

  // ⚡ RESTRICTION SYSTEM FIELDS ⚡
  // ✅ SINGLE SOURCE OF TRUTH - Admin only modifies 'restrictions' array
  // restrictionFlags are AUTO-COMPUTED via Cloud Function

  restrictionFlags?: {
    canCreateListing: boolean;
    canSell: boolean;
    canBuy: boolean;
    canMessage: boolean;
    canWithdraw: boolean;
    accountSuspended: boolean;
    permanentBan: boolean;
  };

  restrictions?: Restriction[]; // Flat array (not nested object)

  // Metrics (auto-updated by Cloud Function)
  warningCount?: number;
  activeRestrictionCount?: number;
  restrictionScore?: number; // 0-100, auto-calculated risk score
  lastRestrictionDate?: number;

  // Optional soft ban
  softBan?: SoftBan;
  blacklistMatches?: number; // Count of blacklist hits
  openDisputes?: number; // For restriction score calculation

  // 🆕 Satıcı Uyarı Sistemi
  sellerWarnings?: {
    count: number;
    lastWarningAt: number;
    reasons: string[];
  };
  temporarySellBanUntil?: number; // 3 uyarı = 7 gün satış yasağı

  // 💳 Marketplace Integration (PayTR/Iyzico)
  subMerchantKey?: string;     // Unique key for the seller in the payment provider
  marketplaceShopId?: string;  // Internal or provider-specific shop identifier
  marketplaceStatus?: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
}

// ========================================
// OFFER SYSTEM (Teklif Sistemi)
// ========================================

export enum OfferStatus {
  PENDING = 'PENDING',                              // Satıcı yanıtı bekliyor
  ACCEPTED = 'ACCEPTED',                            // Kabul edildi - 24 saat satın alma penceresi
  REJECTED = 'REJECTED',                            // Reddedildi - Alıcı yeni teklif verebilir
  COUNTER_OFFERED = 'COUNTER_OFFERED',              // Satıcı karşı teklif yaptı
  BUYER_ACCEPTED_COUNTER = 'BUYER_ACCEPTED_COUNTER', // Alıcı karşı teklifi kabul etti
  BUYER_REJECTED_COUNTER = 'BUYER_REJECTED_COUNTER', // Alıcı karşı teklifi reddetti
  EXPIRED = 'EXPIRED',                              // 24 saat doldu
  PURCHASED = 'PURCHASED',                          // Başarıyla satın alındı
  UNDONE = 'UNDONE'                                 // Satıcı 15 dk içinde geri aldı
}

export interface Offer {
  id: string;
  listingId: string;
  buyerId: string;
  sellerId: string;

  // Fiyat Bilgileri
  originalPrice: number;                  // İlan orijinal fiyatı
  offeredPrice: number;                   // Alıcının teklif ettiği fiyat
  counterOfferPrice?: number;             // Satıcının karşı teklifi
  finalPrice?: number;                    // Kabul edilen son fiyat
  discountPercent: number;                // İndirim yüzdesi

  // Mesajlaşma
  buyerMessage?: string;                  // Alıcının teklif mesajı
  sellerResponse?: string;                // Satıcının yanıt mesajı

  // Durum & Zaman
  status: OfferStatus;
  createdAt: number;
  expiresAt: number;                      // createdAt + 24 saat
  respondedAt?: number;

  // Kabul sonrası
  acceptedAt?: number;
  purchaseDeadline?: number;              // acceptedAt + 24 saat

  // Geri Alma Sistemi (15 dakika penceresi)
  canUndoUntil?: number;                  // acceptedAt + 15 dakika
  isUndone?: boolean;
  undoneAt?: number;
  undoneReason?: string;

  // Destek Müdahalesi
  cancelledBySupport?: boolean;
  supportCancelReason?: string;
  supportAgentId?: string;

  // Sonuç
  resultingOrderId?: string;              // Satın alma tamamlandıysa

  // Ürün Snapshot (teklif anında)
  productSnapshot?: {
    title: string;
    imageUrl: string;
    sellerName?: string;
  };
}

// ========================================
// ORDER SYSTEM
// ========================================

export enum OrderStatus {
  // 🆕 Teklif/Onay Durumları
  AWAITING_SELLER_APPROVAL = 'AWAITING_SELLER_APPROVAL',  // Satıcı onayı bekleniyor
  SELLER_REJECTED = 'SELLER_REJECTED',                    // Satıcı reddetti
  AUTO_EXPIRED = 'AUTO_EXPIRED',                          // Zaman aşımı
  PENDING_PAYMENT = 'PENDING_PAYMENT',                    // Ödeme bekleniyor (Eski, şimdi AUTH sonrası CAPTURE)
  AUTHORIZED = 'AUTHORIZED',                              // Ödeme provizyonu alındı, onay bekleniyor
  PENDING = 'PENDING',                                    // Genel bekleme (Sorgu amaçlı)

  // Mevcut durumlar
  PREPARING = 'PREPARING',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  DISPUTED = 'DISPUTED',
  COMPLETED = 'COMPLETED'
}

export interface Order {
  id: string;
  listingId: string;
  buyerId: string;
  sellerId: string;
  status: OrderStatus;
  price: number;
  shippingInfo?: {
    carrier: string;
    trackingCode: string;
  };
  isSystemCancelled?: boolean;
  escrowReleased?: boolean;
  escrowReleasedAt?: any;
  createdAt: number;
  isReviewed?: boolean;
  disputeId?: string;
  deliveredAt?: number;
  autoReleaseAt?: number;
  productSnapshot?: {
    title: string;
    imageUrl: string;
    price: number;
    sellerName?: string;
  };
  shippingAddress?: Address;
  image?: string;
  marketRegion: 'TR' | 'US' | 'UK'; // Order is locked to this region
  currency: 'TRY' | 'USD' | 'GBP';

  // 🆕 Teklif İlişkisi
  isFromOffer?: boolean;
  offerId?: string;
  specialPrice?: number;                  // Tekliften gelen özel fiyat

  // 🆕 Destek Müdahalesi
  cancelledBySupport?: boolean;
  supportCancelReason?: string;
  supportAgentId?: string;

  // ⚡ Shipping & Financial Details
  shippingPayer?: 'buyer' | 'seller';
  packageSize?: 'xs' | 's' | 'm' | 'l' | 'xl';
  shippingPrice?: number;
  commissionAmount?: number;
  taxAmount?: number;
  referralAmount?: number;
  sellerPayout?: number;

  // ⚖️ Financial Integrity & GİB Reporting (Phase 89)
  productPrice: number;
  buyerCommission: number;
  sellerCommission: number;
  commissionVat: number;
  stopajAmount: number;
  sellerNetPayout: number;
  totalPaidByBuyer: number;
  withholdingTaxRate?: number; // e.g., 0.01 for 1%
  gibReportStatus?: 'PENDING' | 'SENT' | 'FAILED';
}

export interface LedgerEntry {
  id: string;
  orderId: string;
  entity: 'PLATFORM' | 'SELLER' | 'STATE';
  category: 'PRODUCT_ESCROW' | 'COMMISSION_REVENUE' | 'VAT_PAYABLE' | 'WITHHOLDING_PAYABLE' | 'PAYOUT' | 'REFUND';
  amount: number;
  vatIncluded: boolean;
  description: string;
  createdAt: number;
}


export interface ChatMessage {
  id: string;
  senderId: string;
  receiverId: string;
  listingId: string;
  text: string;
  timestamp: number;
  isRead?: boolean; // New: Read receipt status
  type?: 'text' | 'image' | 'location'; // New: Message type support
}




export interface MarketAnalysis {
  suggestedPrice: number;
  confidence: number;
  reasoning: string;
  competitorPrices: {
    source: string;
    price: number;
  }[];
}

export interface BoostTier {
  id: string;
  name: string;
  minPriceUSD: number;
  maxPriceUSD: number;
  percent: number;
  minUSD: number;
  maxUSD: number | null;
  durationDays: number;
  features: string[];
}

export interface CalculatedFee {
  feeTRY: number;
  feeUSD: number;
  tier: BoostTier;
  exchangeRate: number;
  isLuxury: boolean;
  isEmergency: boolean;
}

export interface Balance {
  userId: string;
  available: number;
  pending: number;
  total: number;
  updatedAt: number;
}

export type TransactionType = 'DEPOSIT' | 'WITHDRAWAL' | 'ESCROW_HOLD' | 'ESCROW_RELEASE' | 'PURCHASE' | 'SALE' | 'REFUND' | 'COMMISSION_FEE' | 'REFERRAL_REWARD' | 'BOOST_FEE';

export interface Transaction {
  id: string;
  userId: string;
  type: TransactionType;
  amount: number;
  orderId?: string;
  listingId?: string;
  withdrawalId?: string;
  description: string;
  createdAt: number;
  fromUserId?: string;
  toUserId?: string;
  adminId?: string;
}

export interface Withdrawal {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  amount: number;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  iban: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'READY_FOR_PAYOUT' | 'COMPLETED';
  rejectionReason?: string;
  note?: string;
  createdAt: any;
  approvedAt?: any;
  approvedBy?: string;
}

export interface DepositRequest {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  amount: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  created_at: number;
  payment_method: string;
  payment_note?: string;
  processedAt?: number;
}

export interface PendingOrder {
  id: string;
  buyerId: string;
  sellerId: string;
  listingId: string;
  totalAmount: number;
  status: 'PENDING';
  created_at: number;
  listing_title: string;
  listing_image: string;
}

export interface DisputeEvidence {
  images: string[];
  text: string;
}

export interface Dispute {
  id: string;
  orderId: string;
  buyerId: string;
  sellerId: string;
  reason: string;
  buyerEvidence: DisputeEvidence;
  sellerEvidence?: DisputeEvidence;
  status: 'WAITING_SELLER' | 'UNDER_REVIEW' | 'REFUNDED' | 'REJECTED';
  decisionReason?: 'DAMAGED_PRODUCT' | 'NOT_AS_DESCRIBED' | 'FRAUD_ATTEMPT' | 'INVALID_DISPUTE' | 'OTHER';
  adminId?: string;
  adminNote?: string;
  createdAt: number;
  resolvedAt?: number;
}

export interface SupportTicket {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  subject: string;
  relatedOrderId?: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  agentId?: string; // Current owner
  createdAt: number;
  updatedAt: number;
  lastMessage: string;
  lastMessageAt: number;
  unreadUser: boolean;
  unreadAgent: boolean;
}

export interface SupportMessage {
  id: string;
  senderId: string;
  senderRole: 'user' | 'support' | 'admin' | 'owner';
  text: string;
  attachments?: string[];
  isInternalNote?: boolean;
  createdAt: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// 💳 PAYMENT SYSTEM TYPES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Payment Status - Tracks the lifecycle of a payment
 * Compatible with: iyzico, PayTR, Stripe
 */
export enum PaymentStatus {
  PENDING = 'PENDING',               // Payment initiated
  AWAITING_3D = 'AWAITING_3D',       // Waiting for 3D Secure verification
  PROCESSING = 'PROCESSING',         // Payment being processed
  COMPLETED = 'COMPLETED',           // Payment successful
  FAILED = 'FAILED',                 // Payment failed
  REFUNDED = 'REFUNDED',             // Fully refunded
  PARTIAL_REFUND = 'PARTIAL_REFUND', // Partially refunded
  CANCELLED = 'CANCELLED'            // Cancelled by user
}

/**
 * Payment Type - What is the payment for?
 */
export enum PaymentType {
  WALLET_DEPOSIT = 'WALLET_DEPOSIT',     // Top-up wallet balance
  DIRECT_PURCHASE = 'DIRECT_PURCHASE',   // Buy product directly with card
  BOOST_PAYMENT = 'BOOST_PAYMENT',       // Pay for listing boost
  CART_PURCHASE = 'CART_PURCHASE',       // Buy multiple items from cart
  SUBSCRIPTION = 'SUBSCRIPTION'           // Future: subscription payments
}

/**
 * Payment Method - How is the user paying?
 */
export enum PaymentMethod {
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  WALLET = 'WALLET',
  BANK_TRANSFER = 'BANK_TRANSFER'
}

/**
 * Card Brand - Detected from card number
 */
export type CardBrand = 'visa' | 'mastercard' | 'amex' | 'troy' | 'unknown';

/**
 * Payment Interface - Core payment record
 * Stored in Firestore: /payments/{paymentId}
 */
export interface Payment {
  id: string;
  userId: string;
  type: PaymentType;
  method: PaymentMethod;
  amount: number;
  currency: 'TRY';
  status: PaymentStatus;

  // Provider Data - NEVER log this!
  providerName: 'demo' | 'iyzico' | 'paytr';
  providerPaymentId?: string;
  providerData?: Record<string, unknown>;  // Raw response - never console.log!

  // 3D Secure
  requires3D: boolean;
  threeDSHtml?: string;      // HTML for 3D Secure redirect
  callbackUrl?: string;

  // Card Info (Masked - safe to store)
  cardLastFour?: string;     // e.g., "4242"
  cardBrand?: CardBrand;
  cardHolderName?: string;

  // Idempotency - Duplicate prevention
  idempotencyKey: string;    // Client-generated unique key

  // If Direct Purchase - Payment to Order 1:1 relationship
  orderId?: string;
  listingId?: string;

  // If Wallet Deposit
  balanceAdded?: boolean;    // Flag to prevent double-add

  // Timestamps
  createdAt: number;
  updatedAt: number;
  completedAt?: number;

  // Error Info
  errorCode?: string;
  errorMessage?: string;

  // Metadata (for fraud detection)
  ipAddress?: string;
  userAgent?: string;

  // Cart Purchase Data
  sellerId?: string;           // For CART_PURCHASE (which seller?)
  itemsSnapshot?: CartItem[];  // Snapshot of items at time of payment
}

/**
 * Card Input - Frontend only, NEVER stored in database
 * ⚠️ PCI DSS: This data should only exist during form submission
 */
export interface CardInput {
  cardNumber: string;        // 16 digits, no spaces
  expireMonth: string;       // 01-12
  expireYear: string;        // 2024+ (2 or 4 digits)
  cvc: string;               // 3-4 digits
  cardHolderName: string;
  // saveCard?: boolean;     // Future: tokenization
}

/**
 * Payment Init Request - Sent to Cloud Function
 */
export interface PaymentInitRequest {
  amount: number;
  type: PaymentType;
  card: CardInput;

  // For direct purchase
  listingId?: string;
  offerId?: string;          // If purchasing with accepted offer

  // For Cart Purchase
  sellerId?: string;         // Required for CART_PURCHASE

  // Idempotency
  idempotencyKey: string;
}

/**
 * Payment Init Response - Returned from Cloud Function
 */
export interface PaymentInitResponse {
  success: boolean;
  paymentId: string;

  // 3D Secure
  requires3D: boolean;
  threeDSHtml?: string;      // Render this HTML for 3D redirect
  redirectUrl?: string;

  // If no 3D required, direct result
  status?: 'COMPLETED' | 'FAILED';
  errorCode?: string;
  errorMessage?: string;
}

/**
 * Demo Test Cards - For development/testing
 * Used by DemoProvider to simulate different scenarios
 */
export const DEMO_TEST_CARDS = {
  SUCCESS_VISA: '4111111111111111',
  SUCCESS_MASTERCARD: '5500000000000004',
  REQUIRES_3D: '4012000000000002',
  FAIL_GENERIC: '4000000000000002',
  FAIL_INSUFFICIENT: '4000000000000019',
  FAIL_EXPIRED: '4000000000000069',
  FAIL_BLOCKED: '4000000000000127'
} as const;
// ========================================
// CART SYSTEM (Cloud-First)
// ========================================

export interface CartItem {
  listingId: string;
  sellerId: string;
  quantity: number;
  addedAt: number;
  priceAtAdd: number; // For price change detection
  title: string;
  image: string;
  sellerName?: string;
  maxQuantity: number; // For stock limit
}

export interface CartData {
  items: {
    [listingId: string]: CartItem;
  };
  totalItems: number;
  updatedAt: number;
  lastSyncedAt?: number;
}

export interface PublicQuestion {
  id: string;
  listingId: string;
  sellerId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  question: string;
  answer?: string;
  answeredAt?: number;
  status: 'APPROVED' | 'HIDDEN' | 'PENDING';
  createdAt: number;
  updatedAt: number;
}
